// Sales "Today" — a daily task list combining four signals for one
// salesperson's own requests (this task's brief): unpriced-over-threshold,
// rejected-and-needing-re-pricing, expiring-within-N-days, and
// near-required-delivery-date. See hooks/useToday.js and
// services/requestService.js#getTodayForSalesUser for the exact shape and
// thresholds (read from `settings`, not hardcoded here).
//
// Zero items in every bucket is a normal, good state ("all clear"), not an
// empty-state apology (root CLAUDE.md: an empty state should never
// apologise for a normal outcome).

import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.jsx';
import { useToday } from '../hooks/useToday.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import { formatDate, formatDateTime } from '../utils/formatDate.js';
import styles from './TodayPage.module.css';

function Section({ titleId, titleVars, emptyId, items, onOpen, renderMeta }) {
  const { t } = useLanguage();

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>
        {t(titleId, titleVars)} ({items.length})
      </h2>
      {items.length === 0 ? (
        <p className={styles.muted}>{t(emptyId)}</p>
      ) : (
        <ul className={styles.list}>
          {items.map((item) => (
            <li key={item.id}>
              <div
                className={styles.row}
                role="link"
                tabIndex={0}
                onClick={() => onOpen(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onOpen(item);
                }}
              >
                <span className={`${styles.refCell} tabularNums`}>
                  {item.request_ref_no || item.ref_no}
                </span>
                <span className={styles.meta}>{renderMeta(item)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function TodayPage() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useToday();

  if (isLoading) {
    return <LoadingState variant="rows" rows={4} />;
  }
  if (isError) {
    return <ErrorState messageId="today.errorBody" error={error} onRetry={refetch} />;
  }

  const { unpriced, rejectedNeedingReprice, expiringSoon, neededBySoon, thresholds } = data;
  const isAllClear =
    unpriced.length === 0 &&
    rejectedNeedingReprice.length === 0 &&
    expiringSoon.length === 0 &&
    neededBySoon.length === 0;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('today.title')}</h1>
      <p className={styles.subtitle}>{t('today.subtitle')}</p>

      {isAllClear ? (
        <EmptyState titleId="today.allClearTitle" bodyId="today.allClearBody" />
      ) : (
        <>
          <Section
            titleId="today.section.unpriced.title"
            titleVars={{ hours: thresholds.unpricedHours }}
            emptyId="today.section.unpriced.empty"
            items={unpriced}
            onOpen={(item) => navigate(`/requests/${item.id}`)}
            renderMeta={(item) => formatDateTime(item.assigned_at, lang) || '—'}
          />
          <Section
            titleId="today.section.rejected.title"
            emptyId="today.section.rejected.empty"
            items={rejectedNeedingReprice}
            onOpen={(item) => navigate(`/my-quotations/${item.id}`)}
            renderMeta={(item) => formatDateTime(item.rejected_at, lang) || '—'}
          />
          <Section
            titleId="today.section.expiring.title"
            titleVars={{ days: thresholds.expiryDays }}
            emptyId="today.section.expiring.empty"
            items={expiringSoon}
            onOpen={(item) => navigate(`/my-quotations/${item.id}`)}
            renderMeta={(item) => formatDate(item.valid_until, lang) || '—'}
          />
          <Section
            titleId="today.section.neededBySoon.title"
            emptyId="today.section.neededBySoon.empty"
            items={neededBySoon}
            onOpen={(item) => navigate(`/requests/${item.id}`)}
            renderMeta={(item) => formatDate(item.needed_by, lang) || '—'}
          />
        </>
      )}
    </div>
  );
}
